import { apiFetch } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import type {
  DeliveryStatusRow,
  DeliverySendResult,
  DeliveryDeadLetter,
  AmendmentRow,
  ClientFeedTransform,
  ClientFeedTransformCreatePayload,
  ClientFeedTransformUpdatePayload,
  CompetitionCreatePayload,
  CompetitionDetail,
  CompetitionRow,
  CommonsPage,
  CompetitionTree,
  CompetitionUpdatePayload,
  CsvReport,
  DefinitionRow,
  DefinitionVersionResult,
  DeliveryAssignment,
  DeliveryAssignmentCreatePayload,
  DeliveryAssignmentUpdatePayload,
  DeliveryClient,
  DeliveryClientCreatePayload,
  DeliveryClientUpdatePayload,
  EnvelopeView,
  EventDetail,
  EventRow,
  FeedDefinition,
  FeedDefinitionUpdatePayload,
  FeedPackage,
  FeedPackageCreatePayload,
  FeedPackageUpdatePayload,
  FeedPreviewResult,
  FixtureAmendPayload,
  FixtureCreated,
  FixtureCreatePayload,
  FixtureCount,
  FixtureFormContext,
  FixturePage,
  FixtureQuery,
  MasterRecord,
  MasterRecordPayload,
  MasterSummary,
  ParticipantCreatePayload,
  ParticipantDetail,
  ParticipantRow,
  ParticipantUpdatePayload,
  RoleCreatePayload,
  RoleDetail,
  RoleRow,
  RoleUpdatePayload,
  SportDefinitionDoc,
  VenueCreatePayload,
  VenueDetail,
  VenueRow,
  VenueUpdatePayload,
} from "@/lib/api/types";

export class ApiError extends Error {
  readonly status: number;
  readonly violations: string[];

  constructor(message: string, status: number, violations: string[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.violations = violations;
  }
}

export async function requestJson<T>(
  path: string,
  init?: RequestInit,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  // Reads must be fresh — this is an authoring/data-entry tool, not a CDN surface.
  // `apiFetch` asks for that, and adds the sign-in.
  const response = await apiFetch(
    `${getAdminApiBaseUrl()}${path}`,
    init,
    fetchImpl,
  );
  if (!response.ok) {
    let message = `${init?.method ?? "GET"} ${path} failed: ${response.status}`;
    let violations: string[] = [];
    try {
      const body = (await response.json()) as { detail?: unknown };
      const detail = body.detail;
      if (Array.isArray(detail)) {
        // FastAPI/pydantic 422: [{loc:[...], msg}, ...] — flatten to readable lines.
        violations = detail.map((e) => {
          const item = e as { loc?: unknown[]; msg?: string };
          const loc = (item.loc ?? []).filter((p) => p !== "body").join(".");
          return loc
            ? `${loc}: ${item.msg ?? "invalid"}`
            : (item.msg ?? "invalid");
        });
      } else if (
        detail &&
        typeof detail === "object" &&
        "violations" in detail
      ) {
        violations = (detail as { violations: string[] }).violations;
      } else if (typeof detail === "string") {
        message = detail;
      }
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ApiError(message, response.status, violations);
  }
  return (await response.json()) as T;
}

export function listDefinitions(
  fetchImpl: typeof fetch = fetch,
): Promise<DefinitionRow[]> {
  return requestJson<DefinitionRow[]>("/definitions", undefined, fetchImpl);
}

export function getDefinition(
  sportCode: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SportDefinitionDoc> {
  return requestJson<SportDefinitionDoc>(
    `/definitions/${encodeURIComponent(sportCode)}`,
    undefined,
    fetchImpl,
  );
}

/** A sport's open draft, for resuming authoring. 404s when there is none. */
export function getDraft(
  sportCode: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SportDefinitionDoc> {
  return requestJson<SportDefinitionDoc>(
    `/definitions/${encodeURIComponent(sportCode)}/draft`,
    undefined,
    fetchImpl,
  );
}

export function createDefinition(
  doc: SportDefinitionDoc,
  fetchImpl: typeof fetch = fetch,
): Promise<DefinitionVersionResult> {
  return requestJson<DefinitionVersionResult>(
    "/definitions",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(doc),
    },
    fetchImpl,
  );
}

export function publishVersion(
  sportCode: string,
  version: number,
  fetchImpl: typeof fetch = fetch,
): Promise<DefinitionVersionResult> {
  return requestJson<DefinitionVersionResult>(
    `/definitions/${encodeURIComponent(sportCode)}/versions/${version}/publish`,
    { method: "POST" },
    fetchImpl,
  );
}

/** A page of fixtures. Filtering is a server concern — see the endpoint. */
export function listFixtures(
  query: FixtureQuery = {},
  fetchImpl: typeof fetch = fetch,
): Promise<FixturePage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const suffix = params.size > 0 ? `?${params}` : "";
  return requestJson<FixturePage>(`/fixtures${suffix}`, undefined, fetchImpl);
}

/** Fixtures per sport. `listFixtures` is capped at 50 rows, so counting it
 * under-reports once a sport has a real season. */
export function countFixtures(
  fetchImpl: typeof fetch = fetch,
): Promise<FixtureCount[]> {
  return requestJson<FixtureCount[]>("/fixtures/counts", undefined, fetchImpl);
}

/** Everything the generated fixture-entry screen needs, in one request. */
export function getFixtureForm(
  sport: string,
  type?: string | null,
  config?: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<FixtureFormContext> {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (config) params.set("config", config);
  const query = params.size > 0 ? `?${params}` : "";
  return requestJson<FixtureFormContext>(
    `/sports/${encodeURIComponent(sport)}/fixture-form${query}`,
    undefined,
    fetchImpl,
  );
}

export function getFixture(
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EnvelopeView> {
  return requestJson<EnvelopeView>(
    `/fixtures/${encodeURIComponent(id)}`,
    undefined,
    fetchImpl,
  );
}

export function listParticipants(
  sport?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ParticipantRow[]> {
  const query = sport ? `?sport=${encodeURIComponent(sport)}` : "";
  return requestJson<ParticipantRow[]>(
    `/participants${query}`,
    undefined,
    fetchImpl,
  );
}

export function getParticipant(
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ParticipantDetail> {
  return requestJson<ParticipantDetail>(
    `/participants/${encodeURIComponent(id)}`,
    undefined,
    fetchImpl,
  );
}

export function createParticipant(
  payload: ParticipantCreatePayload,
  fetchImpl: typeof fetch = fetch,
): Promise<ParticipantDetail> {
  return requestJson<ParticipantDetail>(
    "/participants",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
    fetchImpl,
  );
}

export function updateParticipant(
  id: string,
  payload: ParticipantUpdatePayload,
  fetchImpl: typeof fetch = fetch,
): Promise<ParticipantDetail> {
  return requestJson<ParticipantDetail>(
    `/participants/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
    fetchImpl,
  );
}

export function listRoles(
  category?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RoleRow[]> {
  const query = category ? `?category=${encodeURIComponent(category)}` : "";
  return requestJson<RoleRow[]>(`/roles${query}`, undefined, fetchImpl);
}

function post<T>(
  path: string,
  body: unknown,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
    fetchImpl,
  );
}

function patch<T>(
  path: string,
  body: unknown,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
    fetchImpl,
  );
}

// --- Roles ---
export const getRole = (id: string, f: typeof fetch = fetch) =>
  requestJson<RoleDetail>(`/roles/${encodeURIComponent(id)}`, undefined, f);
export const createRole = (p: RoleCreatePayload, f: typeof fetch = fetch) =>
  post<RoleDetail>("/roles", p, f);
export const updateRole = (
  id: string,
  p: RoleUpdatePayload,
  f: typeof fetch = fetch,
) => patch<RoleDetail>(`/roles/${encodeURIComponent(id)}`, p, f);

// --- Competitions ---
/** The whole competition tree in one request, with what each level may hold. */
export const getCompetitionTree = (sport: string, f: typeof fetch = fetch) =>
  requestJson<CompetitionTree>(
    `/competitions/tree?sport=${encodeURIComponent(sport)}`,
    undefined,
    f,
  );

export const listCompetitions = (sport: string, f: typeof fetch = fetch) =>
  requestJson<CompetitionRow[]>(
    `/competitions?sport=${encodeURIComponent(sport)}`,
    undefined,
    f,
  );
export const getCompetition = (id: string, f: typeof fetch = fetch) =>
  requestJson<CompetitionDetail>(
    `/competitions/${encodeURIComponent(id)}`,
    undefined,
    f,
  );
export const createCompetition = (
  p: CompetitionCreatePayload,
  f: typeof fetch = fetch,
) => post<CompetitionDetail>("/competitions", p, f);
export const updateCompetition = (
  id: string,
  p: CompetitionUpdatePayload,
  f: typeof fetch = fetch,
) => patch<CompetitionDetail>(`/competitions/${encodeURIComponent(id)}`, p, f);

// --- Venues ---
export const listVenues = (f: typeof fetch = fetch) =>
  requestJson<VenueRow[]>("/venues", undefined, f);
export const getVenue = (id: string, f: typeof fetch = fetch) =>
  requestJson<VenueDetail>(`/venues/${encodeURIComponent(id)}`, undefined, f);
export const createVenue = (p: VenueCreatePayload, f: typeof fetch = fetch) =>
  post<VenueDetail>("/venues", p, f);
export const updateVenue = (
  id: string,
  p: VenueUpdatePayload,
  f: typeof fetch = fetch,
) => patch<VenueDetail>(`/venues/${encodeURIComponent(id)}`, p, f);

// --- Dynamic masters ---
const mastersPath = (sport: string) =>
  `/sports/${encodeURIComponent(sport)}/masters`;
const recordsPath = (sport: string, master: string) =>
  `${mastersPath(sport)}/${encodeURIComponent(master)}/records`;

/** Records belonging to more than one sport, or to none — the Commons. */
export const listCommons = (q?: string, f: typeof fetch = fetch) =>
  requestJson<CommonsPage>(
    `/commons/records${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    undefined,
    f,
  );

// --- Multi-sport events ---

export const listEvents = (f: typeof fetch = fetch) =>
  requestJson<EventRow[]>("/events", undefined, f);

export const getEvent = (code: string, f: typeof fetch = fetch) =>
  requestJson<EventDetail>(`/events/${encodeURIComponent(code)}`, undefined, f);

export const createEvent = (
  payload: { code: string; name: string; startDate?: string; endDate?: string },
  f: typeof fetch = fetch,
) => post<EventRow>("/events", payload, f);

export const addProgramme = (
  code: string,
  payload: { sportCode: string; name: string; level?: string },
  f: typeof fetch = fetch,
) =>
  post<unknown>(`/events/${encodeURIComponent(code)}/programmes`, payload, f);

export const listMasters = (sport: string, f: typeof fetch = fetch) =>
  requestJson<MasterSummary[]>(mastersPath(sport), undefined, f);

/** One page of records, searched and filtered.
 *
 * `total` counts everything matching, not what came back — a list saying
 * "12 shown" without "of 512" is how someone concludes the rest were deleted.
 */
export const listRecords = (
  sport: string,
  master: string,
  query: {
    q?: string;
    filter?: string[];
    limit?: number;
    offset?: number;
  } = {},
  f: typeof fetch = fetch,
) => {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  for (const one of query.filter ?? []) params.append("filter", one);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  const suffix = params.toString() ? `?${params}` : "";
  return requestJson<{ rows: MasterRecord[]; total: number }>(
    `${recordsPath(sport, master)}${suffix}`,
    undefined,
    f,
  );
};

export const getRecord = (
  sport: string,
  master: string,
  code: string,
  f: typeof fetch = fetch,
) =>
  requestJson<MasterRecord>(
    `${recordsPath(sport, master)}/${encodeURIComponent(code)}`,
    undefined,
    f,
  );

export const createRecord = (
  sport: string,
  master: string,
  payload: MasterRecordPayload,
  f: typeof fetch = fetch,
) => post<MasterRecord>(recordsPath(sport, master), payload, f);

export const updateRecord = (
  sport: string,
  master: string,
  code: string,
  payload: MasterRecordPayload,
  f: typeof fetch = fetch,
) =>
  patch<MasterRecord>(
    `${recordsPath(sport, master)}/${encodeURIComponent(code)}`,
    payload,
    f,
  );

export async function getCsvTemplate(
  sport: string,
  master: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const path = `${mastersPath(sport)}/${encodeURIComponent(master)}/csv-template`;
  const response = await apiFetch(
    `${getAdminApiBaseUrl()}${path}`,
    {},
    fetchImpl,
  );
  if (!response.ok) {
    throw new ApiError(
      `GET ${path} failed: ${response.status}`,
      response.status,
    );
  }
  return response.text();
}

/** Dry-run (commit=false) validates without writing; commit=true imports the
 * whole file or nothing. */
export const uploadCsv = (
  sport: string,
  master: string,
  csv: string,
  commit: boolean,
  f: typeof fetch = fetch,
) =>
  post<CsvReport>(
    `${recordsPath(sport, master).replace(/\/records$/, "")}/csv?commit=${commit}`,
    { csv },
    f,
  );

export function createFixture(
  payload: FixtureCreatePayload,
  fetchImpl: typeof fetch = fetch,
): Promise<FixtureCreated> {
  return requestJson<FixtureCreated>(
    "/fixtures",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
    fetchImpl,
  );
}

/** Edit an existing fixture. Recorded server-side as an amendment (AMEND while
 *  scheduled, CORRECTION once live/completed); returns that amendment. */
export function updateFixture(
  id: string,
  payload: FixtureAmendPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<AmendmentRow> {
  return requestJson<AmendmentRow>(
    `/fixtures/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
    fetchImpl,
  );
}

/** A fixture's append-only amendment history, oldest first. */
export function listAmendments(
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AmendmentRow[]> {
  return requestJson<AmendmentRow[]>(
    `/fixtures/${encodeURIComponent(id)}/amendments`,
    undefined,
    fetchImpl,
  );
}

// --- Outbound delivery control plane ---
function del(path: string, f: typeof fetch = fetch): Promise<Response> {
  return apiFetch(
    `${getAdminApiBaseUrl()}${path}`,
    { method: "DELETE" },
    f,
  ).then((r) => {
    if (!r.ok)
      throw new ApiError(`DELETE ${path} failed: ${r.status}`, r.status);
    return r;
  });
}

// Clients
export const listDeliveryClients = (f: typeof fetch = fetch) =>
  requestJson<DeliveryClient[]>("/delivery/clients", undefined, f);
export const getDeliveryClient = (id: string, f: typeof fetch = fetch) =>
  requestJson<DeliveryClient>(
    `/delivery/clients/${encodeURIComponent(id)}`,
    undefined,
    f,
  );
export const createDeliveryClient = (
  p: DeliveryClientCreatePayload,
  f: typeof fetch = fetch,
) => post<DeliveryClient>("/delivery/clients", p, f);
export const updateDeliveryClient = (
  id: string,
  p: DeliveryClientUpdatePayload,
  f: typeof fetch = fetch,
) => patch<DeliveryClient>(`/delivery/clients/${encodeURIComponent(id)}`, p, f);

// Packages
export const listFeedPackages = (f: typeof fetch = fetch) =>
  requestJson<FeedPackage[]>("/delivery/packages", undefined, f);
export const getFeedPackage = (id: string, f: typeof fetch = fetch) =>
  requestJson<FeedPackage>(
    `/delivery/packages/${encodeURIComponent(id)}`,
    undefined,
    f,
  );
export const createFeedPackage = (
  p: FeedPackageCreatePayload,
  f: typeof fetch = fetch,
) => post<FeedPackage>("/delivery/packages", p, f);
export const updateFeedPackage = (
  id: string,
  p: FeedPackageUpdatePayload,
  f: typeof fetch = fetch,
) => patch<FeedPackage>(`/delivery/packages/${encodeURIComponent(id)}`, p, f);

// Running it: what each client has, what failed, and sending again
export const listDeliveryStatus = (
  params: { clientId?: string; failing?: boolean } = {},
  f: typeof fetch = fetch,
) => {
  const query = new URLSearchParams();
  if (params.clientId) query.set("clientId", params.clientId);
  if (params.failing) query.set("failing", "true");
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return requestJson<DeliveryStatusRow[]>(
    `/delivery/status${suffix}`,
    undefined,
    f,
  );
};
export const listDeadLetters = (resolved = false, f: typeof fetch = fetch) =>
  requestJson<DeliveryDeadLetter[]>(
    `/delivery/deadletters?resolved=${resolved}`,
    undefined,
    f,
  );
/** Try a failed delivery again; the sender picks it up on its next pass. */
export const replayDeadLetter = (id: string, f: typeof fetch = fetch) =>
  post<DeliverySendResult>(
    `/delivery/deadletters/${encodeURIComponent(id)}/replay`,
    {},
    f,
  );
/** Mark a failure handled without sending anything. */
export const resolveDeadLetter = (id: string, f: typeof fetch = fetch) =>
  post<DeliveryDeadLetter>(
    `/delivery/deadletters/${encodeURIComponent(id)}/resolve`,
    {},
    f,
  );
/** Send this client their files again, changed or not. */
export const sendAssignmentNow = (id: string, f: typeof fetch = fetch) =>
  post<DeliverySendResult>(
    `/delivery/assignments/${encodeURIComponent(id)}/send-now`,
    {},
    f,
  );
/** The transports this build can send over. */
export const listDeliveryTransports = (f: typeof fetch = fetch) =>
  requestJson<string[]>("/delivery/transports", undefined, f);

// Feed definitions (persisted queries behind a package's feed keys)
export const listFeedDefinitions = (f: typeof fetch = fetch) =>
  requestJson<FeedDefinition[]>("/delivery/feeds", undefined, f);
export const getFeedDefinition = (code: string, f: typeof fetch = fetch) =>
  requestJson<FeedDefinition>(
    `/delivery/feeds/${encodeURIComponent(code)}`,
    undefined,
    f,
  );
export const updateFeedDefinition = (
  code: string,
  p: FeedDefinitionUpdatePayload,
  f: typeof fetch = fetch,
) =>
  requestJson<FeedDefinition>(
    `/delivery/feeds/${encodeURIComponent(code)}`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(p),
    },
    f,
  );
/** Run the definition's query against the live public API. `event` defaults to
 *  the Asian-Games seriesid server-side; pass one to preview another event. */
export const previewFeedDefinition = (
  code: string,
  event?: string,
  f: typeof fetch = fetch,
) =>
  post<FeedPreviewResult>(
    `/delivery/feeds/${encodeURIComponent(code)}/preview`,
    event ? { event } : {},
    f,
  );

// Assignments
export const listDeliveryAssignments = (
  clientId: string,
  f: typeof fetch = fetch,
) =>
  requestJson<DeliveryAssignment[]>(
    `/delivery/assignments?clientId=${encodeURIComponent(clientId)}`,
    undefined,
    f,
  );
export const createDeliveryAssignment = (
  p: DeliveryAssignmentCreatePayload,
  f: typeof fetch = fetch,
) => post<DeliveryAssignment>("/delivery/assignments", p, f);
export const updateDeliveryAssignment = (
  id: string,
  p: DeliveryAssignmentUpdatePayload,
  f: typeof fetch = fetch,
) =>
  patch<DeliveryAssignment>(
    `/delivery/assignments/${encodeURIComponent(id)}`,
    p,
    f,
  );
export const deleteDeliveryAssignment = (id: string, f: typeof fetch = fetch) =>
  del(`/delivery/assignments/${encodeURIComponent(id)}`, f);

// Transforms
export const listClientFeedTransforms = (
  clientId: string,
  f: typeof fetch = fetch,
) =>
  requestJson<ClientFeedTransform[]>(
    `/delivery/transforms?clientId=${encodeURIComponent(clientId)}`,
    undefined,
    f,
  );
export const createClientFeedTransform = (
  p: ClientFeedTransformCreatePayload,
  f: typeof fetch = fetch,
) => post<ClientFeedTransform>("/delivery/transforms", p, f);
export const updateClientFeedTransform = (
  id: string,
  p: ClientFeedTransformUpdatePayload,
  f: typeof fetch = fetch,
) =>
  patch<ClientFeedTransform>(
    `/delivery/transforms/${encodeURIComponent(id)}`,
    p,
    f,
  );
export const deleteClientFeedTransform = (
  id: string,
  f: typeof fetch = fetch,
) => del(`/delivery/transforms/${encodeURIComponent(id)}`, f);
